import { DesktopAdaptiveFps } from "./desktop-adaptive-fps.js";
import { videoConfigPacket, videoFramePacket } from "./video-protocol.js";

const DESKTOP_CODEC = { codec: "avc1.420028", label: "H.264 FullHD", avc: { format: "annexb" } };
const DESKTOP_QUALITY = "high";
const DESKTOP_WIDTH = 1920;
const DESKTOP_HEIGHT = 1080;
const DESKTOP_BITRATE = 1_500_000;
/** Как часто слать keyframe, даже если экран не меняется. */
const KEYFRAME_INTERVAL_MS = 2000;
/** Как часто проверять «давно не было кадра». */
const IDLE_TICK_MS = 250;

/**
 * FullHD H.264 desktop share with adaptive encode FPS (8–15).
 * Publishes to the same high video subject as camera high stream.
 *
 * Для тебя: браузер часто не шлёт кадры при статичном экране.
 * Без idle-keyframe поздний зритель ждёт движения мыши.
 */
export class DesktopPublisher {
  constructor({ publishPacket, onError = console.error, onState = () => {}, onEnded = () => {} }) {
    this.publishPacket = publishPacket;
    this.onError = onError;
    this.onState = onState;
    this.onEnded = onEnded;
    this.fpsController = new DesktopAdaptiveFps({
      minFps: 8,
      maxFps: 15,
      startFps: 10,
      targetBitrate: DESKTOP_BITRATE,
      windowSize: 10
    });
    this.pendingSendBytes = 0;
    this.running = false;
    this.nextEncodeAt = 0;
    this.lastKeyframeAt = 0;
    this.lastEncodeAt = 0;
    this.forceNextKeyframe = true;
    this.hasDrawnFrame = false;
    this.idleTimer = undefined;
  }

  /**
   * Запускает захват экрана и кодирование.
   *
   * @param {{ mediaStream: MediaStream, preview: HTMLVideoElement }} options
   */
  async start({ mediaStream, preview }) {
    if (!globalThis.VideoEncoder || !globalThis.MediaStreamTrackProcessor) {
      throw new Error("This browser needs WebCodecs VideoEncoder for desktop share.");
    }

    const track = mediaStream.getVideoTracks()[0];
    if (!track) {
      throw new Error("No screen video track.");
    }

    this.mediaStream = mediaStream;
    this.preview = preview;
    this.running = true;
    this.track = track;
    this.trackEndedHandler = () => {
      if (this.running) {
        this.onEnded();
      }
    };
    track.addEventListener("ended", this.trackEndedHandler);

    const encoderConfig = {
      codec: DESKTOP_CODEC.codec,
      width: DESKTOP_WIDTH,
      height: DESKTOP_HEIGHT,
      bitrate: DESKTOP_BITRATE,
      framerate: 15,
      latencyMode: "realtime",
      hardwareAcceleration: "no-preference",
      avc: DESKTOP_CODEC.avc
    };
    const support = await VideoEncoder.isConfigSupported(encoderConfig);
    if (!support.supported) {
      throw new Error("H.264 FullHD encoder is not supported on this device.");
    }

    this.desktopTrack = {
      width: DESKTOP_WIDTH,
      height: DESKTOP_HEIGHT,
      quality: DESKTOP_QUALITY,
      codec: DESKTOP_CODEC,
      decoderConfig: undefined,
      pendingAccessUnit: undefined,
      publishing: false,
      stopped: false,
      canvas: Object.assign(document.createElement("canvas"), {
        width: DESKTOP_WIDTH,
        height: DESKTOP_HEIGHT
      })
    };

    this.encoder = new VideoEncoder({
      output: (chunk, metadata) => {
        this.fpsController.onEncodedChunk(chunk);
        if (metadata?.decoderConfig) {
          this.desktopTrack.decoderConfig = metadata.decoderConfig;
        }
        const configPacket = chunk.type === "key" && this.desktopTrack.decoderConfig
          ? videoConfigPacket(this.desktopTrack.decoderConfig, this.desktopTrack, DESKTOP_CODEC)
          : undefined;
        this.queueAccessUnit(configPacket, videoFramePacket(chunk));
      },
      error: error => this.onError(error)
    });
    this.encoder.configure(support.config);
    this.nextEncodeAt = 0;
    this.lastEncodeAt = 0;
    this.lastKeyframeAt = 0;
    this.forceNextKeyframe = true;
    this.hasDrawnFrame = false;
    this.onState({
      label: `desktop: H.264 FullHD · adaptive 8–15 fps · ${Math.round(DESKTOP_BITRATE / 1000)} kbps · key ${KEYFRAME_INTERVAL_MS}ms`
    });

    const processor = new MediaStreamTrackProcessor({ track });
    this.frameReader = processor.readable.getReader();
    this.startIdleKeyframeTimer();
    void this.consumeFrames();
  }

  /**
   * Останавливает захват, таймер и encoder.
   */
  stop() {
    this.running = false;
    this.stopIdleKeyframeTimer();
    if (this.track && this.trackEndedHandler) {
      this.track.removeEventListener("ended", this.trackEndedHandler);
    }
    this.trackEndedHandler = undefined;
    this.frameReader?.cancel().catch(() => {});
    this.frameReader = undefined;
    if (this.desktopTrack) {
      this.desktopTrack.stopped = true;
      this.desktopTrack.pendingAccessUnit = undefined;
    }
    this.encoder?.close();
    this.encoder = undefined;
    this.mediaStream?.getVideoTracks().forEach(videoTrack => videoTrack.stop());
    this.mediaStream = undefined;
    this.preview = undefined;
    this.pendingSendBytes = 0;
    this.hasDrawnFrame = false;
  }

  /**
   * Таймер: раз в секунду шлём keyframe с последнего canvas,
   * даже если трек молчит (статичный экран).
   *
   * For maintainers: getDisplayMedia often stops delivering frames until pixels change.
   */
  startIdleKeyframeTimer() {
    this.stopIdleKeyframeTimer();
    this.idleTimer = setInterval(() => {
      this.encodeIdleKeyframeIfNeeded();
    }, IDLE_TICK_MS);
  }

  /**
   * Гасит idle-таймер.
   */
  stopIdleKeyframeTimer() {
    if (this.idleTimer !== undefined) {
      clearInterval(this.idleTimer);
      this.idleTimer = undefined;
    }
  }

  /**
   * Если давно не было encode — перекодирует последний canvas как keyframe.
   */
  encodeIdleKeyframeIfNeeded() {
    if (!this.running || !this.encoder || !this.hasDrawnFrame) {
      return;
    }
    const now = performance.now();
    if (now - this.lastKeyframeAt < KEYFRAME_INTERVAL_MS) {
      return;
    }
    if (this.encoder.encodeQueueSize > 2) {
      return;
    }
    this.forceNextKeyframe = true;
    this.encodeCanvasFrame(now);
  }

  /**
   * Читает кадры с экрана и кодирует с адаптивным FPS.
   */
  async consumeFrames() {
    try {
      while (this.running && this.frameReader) {
        const { done, value: frame } = await this.frameReader.read();
        if (done) {
          break;
        }
        this.onCaptureFrame(frame);
        frame.close();
      }
    } catch (error) {
      if (this.running) {
        this.onError(error);
      }
    }
  }

  /**
   * Обрабатывает один кадр с трека (если пора по FPS).
   *
   * @param {VideoFrame} sourceFrame
   */
  onCaptureFrame(sourceFrame) {
    if (!this.running || !this.encoder) {
      return;
    }

    const now = performance.now();
    const targetFps = this.fpsController.update({
      encoderQueueSize: this.encoder.encodeQueueSize,
      sendQueueBytes: this.pendingSendBytes
    });
    const intervalMs = 1000 / targetFps;

    if (now < this.nextEncodeAt) {
      return;
    }

    this.fitFrameToCanvas(sourceFrame);
    this.encodeCanvasFrame(now);
    this.nextEncodeAt = now + intervalMs;
  }

  /**
   * Кодирует текущий canvas. Keyframe — по таймеру или force.
   *
   * @param {number} now
   */
  encodeCanvasFrame(now) {
    if (!this.running || !this.encoder || !this.desktopTrack?.canvas) {
      return;
    }
    const timestamp = Math.round(now * 1000);
    const keyFrame = this.shouldSendKeyframe(now);
    let frame;
    try {
      frame = new VideoFrame(this.desktopTrack.canvas, { timestamp });
      this.encoder.encode(frame, { keyFrame });
      this.lastEncodeAt = now;
    } catch (error) {
      this.onError(error);
    } finally {
      frame?.close();
    }
  }

  /**
   * Нужен ли keyframe сейчас.
   *
   * @param {number} now
   * @returns {boolean}
   */
  shouldSendKeyframe(now) {
    if (this.forceNextKeyframe) {
      this.forceNextKeyframe = false;
      this.lastKeyframeAt = now;
      return true;
    }
    if (now - this.lastKeyframeAt >= KEYFRAME_INTERVAL_MS) {
      this.lastKeyframeAt = now;
      return true;
    }
    return false;
  }

  /**
   * Рисует кадр в 1920x1080 с letterbox — без обрезки и без растягивания.
   *
   * @param {VideoFrame} sourceFrame
   */
  fitFrameToCanvas(sourceFrame) {
    const srcW = sourceFrame.displayWidth;
    const srcH = sourceFrame.displayHeight;
    const scale = Math.min(DESKTOP_WIDTH / srcW, DESKTOP_HEIGHT / srcH);
    const drawW = Math.max(1, Math.round(srcW * scale));
    const drawH = Math.max(1, Math.round(srcH * scale));
    const canvas = this.desktopTrack.canvas;
    canvas.width = DESKTOP_WIDTH;
    canvas.height = DESKTOP_HEIGHT;
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, DESKTOP_WIDTH, DESKTOP_HEIGHT);
    const offsetX = Math.floor((DESKTOP_WIDTH - drawW) / 2);
    const offsetY = Math.floor((DESKTOP_HEIGHT - drawH) / 2);
    ctx.drawImage(sourceFrame, offsetX, offsetY, drawW, drawH);
    this.hasDrawnFrame = true;
  }

  /**
   * Кладёт config+frame в очередь публикации (оставляет только последний AU).
   *
   * @param {Uint8Array|undefined} config
   * @param {Uint8Array} frame
   */
  queueAccessUnit(config, frame) {
    this.desktopTrack.pendingAccessUnit = { config, frame };
    if (!this.desktopTrack.publishing) {
      void this.publishLatest();
    }
  }

  /**
   * Публикует последние access unit по порядку.
   */
  async publishLatest() {
    const state = this.desktopTrack;
    if (!state) {
      return;
    }
    state.publishing = true;
    try {
      while (state.pendingAccessUnit && !state.stopped && this.running) {
        const accessUnit = state.pendingAccessUnit;
        state.pendingAccessUnit = undefined;
        if (accessUnit.config) {
          await this.publishQueued(DESKTOP_QUALITY, accessUnit.config);
        }
        await this.publishQueued(DESKTOP_QUALITY, accessUnit.frame);
      }
    } catch (error) {
      this.onError(error);
    } finally {
      state.publishing = false;
      if (state.pendingAccessUnit && !state.stopped && this.running) {
        void this.publishLatest();
      }
    }
  }

  /**
   * Публикует один пакет и учитывает размер очереди отправки.
   *
   * @param {string} quality
   * @param {Uint8Array} payload
   */
  async publishQueued(quality, payload) {
    this.pendingSendBytes += payload.byteLength;
    try {
      await this.publishPacket(quality, payload);
    } finally {
      this.pendingSendBytes = Math.max(0, this.pendingSendBytes - payload.byteLength);
    }
  }
}
